// Etsy research tools on Alura data — PEXIFY team only
// (spec docs/superpowers/specs/2026-11-01-etsy-research-alura-admin.md).
//
// Keyword, shop, review and listing lookups read Alura with PEXIFY's one key.
// Every lookup spends one of the account's daily searches and Alura API
// credits; the same query on the same day is answered from the day's copy and
// spends neither. Today's opportunities only re-read the day's searches.
// Alura's API Terms keep this data inside the PEXIFY team, so only the admin
// shell calls these routes. The backend does all the mapping — this module
// only carries the shapes.
import { apiUrl } from '../lib/api'

/** 0–30, 31–60, 61–90, 91–180, 181+ days since the listing went up. */
export type AgeGroup = 'a' | 'b' | 'c' | 'd' | 'e'

export const AGE_GROUPS: readonly AgeGroup[] = ['a', 'b', 'c', 'd', 'e']

export type SearchKind = 'keyword' | 'shop'

export interface EtsyResearchUsage {
  day: string
  used: number
  limit: number
  /** Alura API credits left this month, from the newest lookup; null when not known. */
  credits_remaining: number | null
}

// `est_*` are Alura's estimates; trends are fractions (0.12 = +12%); null is
// unknown, never zero.
export interface ResearchListing {
  listing_id: number
  title: string
  url: string | null
  image_url: string | null
  shop_id: number | null
  shop_name: string | null
  listed_at: string | null
  age_days: number | null
  age_group: AgeGroup | null
  favorites: number | null
  review_count: number | null
  review_average: number | null
  est_monthly_sales: number | null
  est_last_month: number | null
  est_trend_3m: number | null
  price: string | null
  currency: string | null
  price_usd: string | null
  /** Place in the shop's best sellers or the keyword's top results, 1 = first. */
  position: number | null
}

export interface SalesMonth {
  month: string
  est_sales: number | null
  est_revenue_usd: number | null
  reviews: number | null
}

export interface ListingDetail extends ResearchListing {
  tags: string[]
  materials: string[]
  category_path: string[]
  images: string[]
  state: string | null
  is_digital: boolean | null
  is_personalizable: boolean | null
  est_monthly_revenue_usd: number | null
  est_last_month_revenue_usd: number | null
  est_lifetime_sales: number | null
}

export interface ShopSummary {
  shop_id: number
  shop_name: string
  url: string | null
  icon_url: string | null
  country: string | null
  currency: string | null
  opened_at: string | null
  star_seller: boolean | null
  /** Etsy's own count of the shop's sales. */
  total_sales: number | null
  review_count: number | null
  review_average: number | null
  active_listings: number | null
  avg_price_usd: number | null
  favorites: number | null
  est_monthly_sales: number | null
  est_last_month: number | null
  est_last_month_revenue_usd: number | null
  est_trend_3m: number | null
}

export interface ListingResearch {
  usage: EtsyResearchUsage
  cached: boolean
  listing: ListingDetail
  history: SalesMonth[]
  /** Alura refused the history: it needs Alura's Growth plan. */
  history_locked: boolean
}

export interface ShopResearch {
  usage: EtsyResearchUsage
  cached: boolean
  shop: ShopSummary
  /** The shop's 25 best sellers, best first. */
  listings: ResearchListing[]
  history: SalesMonth[]
  /** Alura refused the history: it needs Alura's Growth plan. */
  history_locked: boolean
}

export interface ShopReview {
  rating: number | null
  /** Written by the buyer — shown as text only. */
  text: string | null
  language: string | null
  image_url: string | null
  created_at: string | null
  listing_id: number | null
  listing_title: string | null
}

export interface ReviewResearch {
  usage: EtsyResearchUsage
  cached: boolean
  shop: ShopSummary
  /** Alura is still counting a large shop's reviews; the counts are unknown. */
  summary_pending: boolean
  review_count: number | null
  average: number | null
  with_images: number | null
  ratings: { stars: number; count: number | null }[]
  /** The 25 newest reviews. */
  reviews: ShopReview[]
  top_listings: { listing_id: number; title: string | null; reviews: number }[]
}

export interface KeywordMetrics {
  etsy_monthly: number | null
  google_monthly: number | null
  competition_level: string | null
  competing_listings: number | null
  change_3m: number | null
  change_yoy: number | null
  avg_price_usd: number | null
}

export interface KeywordShop {
  shop_id: number
  shop_name: string | null
  /** How many of the top results are this shop's. */
  listings: number
}

export interface KeywordResearch {
  usage: EtsyResearchUsage
  cached: boolean
  keyword: string
  metrics: KeywordMetrics
  total_results: number
  listings: ResearchListing[]
  shops: KeywordShop[]
}

export interface FoundIn {
  kind: SearchKind
  query: string
}

export interface OpportunityListing extends ResearchListing {
  found_in: FoundIn[]
}

export interface TodaySearch extends FoundIn {
  searched_at: string
  listings: number
}

export interface Opportunities {
  usage: EtsyResearchUsage
  day: string
  searches: TodaySearch[]
  listings: OpportunityListing[]
}

export type EtsyResearchErrorCode =
  | 'daily_limit'
  | 'not_found'
  | 'invalid_query'
  | 'alura_pending'
  | 'alura_not_configured'
  | 'alura_credits'
  | 'alura_plan'
  | 'alura_unavailable'

export class EtsyResearchError extends Error {
  readonly status: number
  readonly code: EtsyResearchErrorCode | null
  /** Seconds until Alura has fetched the item, when it said. */
  readonly retryAfter: number | null

  constructor(
    message: string,
    status: number,
    code: EtsyResearchErrorCode | null,
    retryAfter: number | null = null,
  ) {
    super(message)
    this.status = status
    this.code = code
    this.retryAfter = retryAfter
  }
}

const BASE = '/admin/etsy-research'

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(apiUrl(`${BASE}${path}`), {
    ...init,
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      detail?: { code?: EtsyResearchErrorCode; message?: string; retry_after?: number }
    } | null
    throw new EtsyResearchError(
      body?.detail?.message ?? `etsy research ${response.status}`,
      response.status,
      body?.detail?.code ?? null,
      body?.detail?.retry_after ?? null,
    )
  }
  return (await response.json()) as T
}

function lookup<T>(path: string, query: string) {
  return call<T>(path, { method: 'POST', body: JSON.stringify({ query }) })
}

export const etsyResearchKeys = {
  usage: ['etsy-research', 'usage'] as const,
  /** Alura credits left, as the newest new lookup reported them (never fetched). */
  credits: ['etsy-research', 'credits'] as const,
  opportunities: ['etsy-research', 'opportunities'] as const,
}

export function fetchUsage() {
  return call<EtsyResearchUsage>('/usage')
}

export function lookupListing(query: string) {
  return lookup<ListingResearch>('/listing', query)
}

export function lookupShop(query: string) {
  return lookup<ShopResearch>('/shop', query)
}

export function lookupShopReviews(query: string) {
  return lookup<ReviewResearch>('/shop-reviews', query)
}

export function lookupKeyword(query: string) {
  return lookup<KeywordResearch>('/keyword', query)
}

export function fetchOpportunities() {
  return call<Opportunities>('/opportunities')
}
